import { type RefObject, useEffect } from 'react';

// Set on <html> while the AI Studio side panel is open; index.scss uses it
// to shift absolutely-anchored page overlays (e.g. the builders' floating
// tab buttons) left of the panel.
export const SIDE_PANEL_ATTR = 'data-ai-studio-side-panel';

// Set on every NavbarV2 root; pages with showNavBar: false render one
// inside .js-main-content-wrapper, and the hook below positions the panel
// below it.
export const NAVBAR_V2_CLASS = 'js-navbar-v2';

// Publishes the AI Studio side panel's global signals on <html> while it
// is open:
//  - --ai-studio-panel-top/-height: the main content box — starting below
//    an in-page NavbarV2, if any — consumed by AIStudioModal's
//    body-portaled panel root so it aligns beside the page content.
//  - SIDE_PANEL_ATTR: consumed by index.scss's page-overlay shift rule.
//
// Re-measures (rAF-coalesced) on resize, content height changes, trial
// banner mount/dismiss, and wrapper subtree changes (a late-mounting
// in-page navbar).
export const useAIStudioSidePanelSignals = ({
  active,
  shellRef,
  wrapperRef,
}: {
  active: boolean;
  shellRef: RefObject<HTMLDivElement | null>;
  wrapperRef: RefObject<HTMLDivElement | null>;
}) => {
  useEffect(() => {
    if (!active) {
      return;
    }
    const wrapper = wrapperRef.current;
    const shell = shellRef.current;
    if (!wrapper || !shell) {
      return;
    }
    const root = document.documentElement;
    // An in-page NavbarV2 (NAVBAR_V2_CLASS), if any, sits at the top of
    // the measured box — the panel must start below it.
    let observedNavbar: Element | null = null;
    const navbarObserver = new ResizeObserver(() => schedule());
    const apply = () => {
      const wrapperRect = wrapper.getBoundingClientRect();
      const navbar = wrapper.querySelector(`.${NAVBAR_V2_CLASS}`);
      const navbarRect = navbar?.getBoundingClientRect();
      const top = Math.max(wrapperRect.top, navbarRect?.bottom ?? 0);
      root.style.setProperty('--ai-studio-panel-top', `${top}px`);
      root.style.setProperty(
        '--ai-studio-panel-height',
        `${wrapperRect.bottom - top}px`,
      );
      if (navbar !== observedNavbar) {
        navbarObserver.disconnect();
        if (navbar) {
          navbarObserver.observe(navbar);
        }
        observedNavbar = navbar;
      }
    };
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(apply);
    };
    apply();
    root.setAttribute(SIDE_PANEL_ATTR, 'open');
    window.addEventListener('resize', schedule);
    const resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(wrapper);
    const bannerObserver = new MutationObserver(schedule);
    bannerObserver.observe(shell, { childList: true });
    // An in-page navbar may mount after the panel opens (page data still
    // loading) — re-measure when the wrapper's subtree changes.
    bannerObserver.observe(wrapper, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      resizeObserver.disconnect();
      navbarObserver.disconnect();
      bannerObserver.disconnect();
      root.removeAttribute(SIDE_PANEL_ATTR);
      root.style.removeProperty('--ai-studio-panel-top');
      root.style.removeProperty('--ai-studio-panel-height');
    };
  }, [active, shellRef, wrapperRef]);
};
